import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Plus,
  Eye,
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Loader2,
  FileSpreadsheet
} from 'lucide-react';
import { getCurrentBodyCorporateId, getCurrentProfileSafe, getCurrentUserId, supabase, Transaction, Account, Flat, Owner } from '../lib/supabase';
import toast from 'react-hot-toast';
import { ExportService } from '../services/ExportService';
import { formatDate, getLocalDateString, toLevyPeriodDate, toLevyPeriodInput } from '../lib/dateUtils';
import { getTransactionTypeLabel } from '../services/LevyService';
import AuditService from '../services/AuditService';

interface TransactionWithDetails extends Transaction {
  accounts?: Account & {
    flats?: Flat;
    owners?: Owner[];
  };
}

type TransactionWithAccount = Transaction & {
  accounts?: Account & {
    flats?: Flat;
  };
};

const Transactions: React.FC = () => {
  const [transactions, setTransactions] = useState<TransactionWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [transactionRole, setTransactionRole] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedTransaction, setSelectedTransaction] = useState<TransactionWithDetails | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showReverseModal, setShowReverseModal] = useState(false);
  const [reverseReason, setReverseReason] = useState('');
  const [reversing, setReversing] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [editForm, setEditForm] = useState({
    transaction_date: getLocalDateString(),
    transaction_type: 'other_charge' as Transaction['transaction_type'],
    description: '',
    reference_number: '',
    due_date: '',
    debit_amount: '0',
    credit_amount: '0',
    notes: '',
    levy_period: '',
    status: 'posted' as Transaction['status'],
  });

  const [bodyCorporateId, setBodyCorporateId] = useState<string | null>(null);
  const navigate = useNavigate();
  const exportService = new ExportService(bodyCorporateId || '');

  const transactionTypes = [
    { value: 'opening_balance', label: 'Opening Balance' },
    { value: 'monthly_levy', label: 'Monthly Levy' },
    { value: 'quarterly_levy', label: 'Quarterly Levy' },
    { value: 'special_levy', label: 'Special Levy' },
    { value: 'maintenance_levy', label: 'Maintenance Levy' },
    { value: 'payment', label: 'Payment' },
    { value: 'penalty', label: 'Penalty' },
    { value: 'interest', label: 'Interest' },
    { value: 'administrative_charge', label: 'Administrative Charge' },
    { value: 'repair_charge', label: 'Repairs / Approved Charge' },
    { value: 'other_charge', label: 'Other Approved Charge' },
    { value: 'miscellaneous_charge', label: 'Miscellaneous Charge' },
    { value: 'credit_adjustment', label: 'Credit Adjustment' },
    { value: 'reversal', label: 'Reversal' },
  ];

  const statusOptions = [
    { value: 'posted', label: 'Posted' },
    { value: 'reversed', label: 'Reversed' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'draft', label: 'Draft' },
  ];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const load = async () => {
      const profile = await getCurrentProfileSafe();
      setTransactionRole(profile?.role || 'staff');

      const id = await getCurrentBodyCorporateId();
      setBodyCorporateId(id);
      await fetchTransactions(id);
    };

    load();
  }, [filterType, filterStatus, dateFrom, dateTo]);

  const fetchTransactions = async (id: string | null) => {
    setLoading(true);

    if (!id) {
      setTransactions([]);
      setLoading(false);
      toast.error('No body corporate is configured for this user.');
      return;
    }

    try {
      let query = supabase
        .from('transactions')
        .select(`
          *,
          accounts (
            id,
            account_number,
            flat_id,
            flats (
              id,
              flat_number,
              physical_address
            )
          )
        `)
        .eq('body_corporate_id', id)
        .order('transaction_date', { ascending: false })
        .order('created_at', { ascending: false });

      if (filterType) {
        query = query.eq('transaction_type', filterType);
      }

      if (filterStatus) {
        query = query.eq('status', filterStatus);
      }

      if (dateFrom) {
        query = query.gte('transaction_date', dateFrom);
      }

      if (dateTo) {
        query = query.lte('transaction_date', dateTo);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Get owners for each account
      const transactionsWithOwners = await Promise.all(
        (data || []).map(async (t: TransactionWithAccount) => {
          const { data: accountOwners } = await supabase
            .from('account_owners')
            .select(`
              owner_id,
              owners (
                id,
                full_name,
                email
              )
            `)
            .eq('account_id', t.account_id)
            .eq('is_primary', true)
            .single();

          return {
            ...t,
            accounts: {
              ...t.accounts,
              owners: accountOwners?.owners ? [accountOwners.owners] : [],
            },
          };
        })
      );

      setTransactions(transactionsWithOwners);
    } catch (error) {
      console.error(error);
      toast.error('Error fetching transactions');
    }
    setLoading(false);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const openEditModal = () => {
    if (!selectedTransaction) return;

    setEditForm({
      transaction_date: selectedTransaction.transaction_date,
      transaction_type: selectedTransaction.transaction_type,
      description: selectedTransaction.description,
      reference_number: selectedTransaction.reference_number || '',
      due_date: selectedTransaction.due_date || '',
      debit_amount: String(selectedTransaction.debit_amount || 0),
      credit_amount: String(selectedTransaction.credit_amount || 0),
      notes: selectedTransaction.notes || '',
      levy_period: toLevyPeriodInput(selectedTransaction.levy_period),
      status: selectedTransaction.status,
    });

    setShowDetailModal(false);
    setShowEditModal(true);
  };

  const handleEditTransaction = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTransaction) return;

    const debit = parseFloat(editForm.debit_amount) || 0;
    const credit = parseFloat(editForm.credit_amount) || 0;

    if (!editForm.description.trim()) {
      toast.error('Description is required');
      return;
    }

    if (debit < 0 || credit < 0) {
      toast.error('Debit and credit amounts must be positive');
      return;
    }

    try {
      setSavingEdit(true);
      const changes = {
        transaction_date: editForm.transaction_date,
        transaction_type: editForm.transaction_type,
        description: editForm.description.trim(),
        reference_number: editForm.reference_number.trim() || null,
        due_date: editForm.due_date || null,
        debit_amount: debit,
        credit_amount: credit,
        notes: editForm.notes.trim(),
        levy_period: ['monthly_levy', 'quarterly_levy', 'special_levy', 'maintenance_levy'].includes(editForm.transaction_type)
          ? toLevyPeriodDate(editForm.levy_period || editForm.transaction_date) : null,
        status: editForm.status,
      };
      const { error } = await supabase
        .from('transactions')
        .update(changes)
        .eq('id', selectedTransaction.id);

      if (error) throw error;

      const userId = await getCurrentUserId();
      if (bodyCorporateId && userId) await new AuditService(bodyCorporateId, userId).logAction('UPDATE', 'transaction', selectedTransaction.id, selectedTransaction, { ...selectedTransaction, ...changes });

      toast.success('Transaction updated successfully');
      setShowEditModal(false);
      setSelectedTransaction(null);
      await fetchTransactions(bodyCorporateId);
    } catch (error) {
      console.error(error);
      toast.error('Error updating transaction');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleReverseTransaction = async () => {
    if (transactionRole === 'viewer') {
      toast.error('Viewer accounts can only view transactions.');
      return;
    }

    if (!selectedTransaction || !reverseReason) {
      toast.error('Please provide a reason for reversal');
      return;
    }

    setReversing(true);
    try {
      const currentUserId = await getCurrentUserId();
      // A reversal is a new, opposite ledger entry.  The original must remain
      // posted so that the two entries net to zero in every balance and
      // statement.  Marking the original as reversed while excluding it from
      // ledger queries would incorrectly leave a credit/debit behind.
      const { data: existingReversal, error: existingReversalError } = await supabase
        .from('transactions')
        .select('id')
        .eq('reversal_of_transaction_id', selectedTransaction.id)
        .eq('status', 'posted')
        .maybeSingle();

      if (existingReversalError) throw existingReversalError;
      if (existingReversal) {
        toast.error('This transaction has already been reversed');
        return;
      }

      // Create reversal transaction
      const { data: reversal, error: reversalError } = await supabase
        .from('transactions')
        .insert({
          body_corporate_id: bodyCorporateId,
          account_id: selectedTransaction.account_id,
          transaction_date: getLocalDateString(),
          transaction_type: 'reversal',
          description: `Reversal: ${selectedTransaction.description}`,
          reference_number: `REV-${selectedTransaction.reference_number || selectedTransaction.id}`,
          debit_amount: selectedTransaction.credit_amount || 0,
          credit_amount: selectedTransaction.debit_amount || 0,
          status: 'posted',
          reversal_of_transaction_id: selectedTransaction.id,
          notes: reverseReason,
          created_by: currentUserId,
        })
        .select()
        .single();

      if (reversalError) throw reversalError;

      if (bodyCorporateId && currentUserId && reversal) {
        const audit = new AuditService(bodyCorporateId, currentUserId);
        await audit.logAction('REVERSE', 'transaction', selectedTransaction.id, selectedTransaction, { ...selectedTransaction, reversal_transaction_id: reversal.id, reason: reverseReason });
        await audit.logAction('CREATE', 'transaction', reversal.id, undefined, reversal);
      }

      toast.success('Transaction reversed successfully');
      setShowReverseModal(false);
      setReverseReason('');
      setSelectedTransaction(null);
      fetchTransactions(bodyCorporateId);
    } catch (error) {
      console.error(error);
      toast.error('Error reversing transaction');
    }
    setReversing(false);
  };

  const handleExport = async () => {
    if (transactionRole === 'viewer') {
      toast.error('Viewer accounts can only view transactions and cannot generate exports.');
      return;
    }

    setExporting(true);
    try {
      const csvContent = await exportService.exportTransactionsToCSV(
        undefined,
        dateFrom || undefined,
        dateTo || undefined
      );
      exportService.downloadCSV(csvContent, `transactions-${new Date().toISOString().split('T')[0]}.csv`);
      toast.success('Export successful');
    } catch (error) {
      console.error(error);
      toast.error('Error exporting transactions');
    }
    setExporting(false);
  };

  const filteredTransactions = transactions.filter((t) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      t.description.toLowerCase().includes(searchLower) ||
      t.reference_number?.toLowerCase().includes(searchLower) ||
      t.accounts?.account_number?.toLowerCase().includes(searchLower) ||
      t.accounts?.flats?.flat_number?.toLowerCase().includes(searchLower)
    );
  });

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'payment': return 'bg-green-100 text-green-800';
      case 'monthly_levy':
      case 'quarterly_levy':
      case 'special_levy':
      case 'maintenance_levy': return 'bg-blue-100 text-blue-800';
      case 'penalty':
      case 'interest': return 'bg-red-100 text-red-800';
      case 'administrative_charge':
      case 'repair_charge':
      case 'miscellaneous_charge':
      case 'other_charge': return 'bg-purple-100 text-purple-800';
      case 'credit_adjustment': return 'bg-teal-100 text-teal-800';
      case 'opening_balance': return 'bg-gray-100 text-gray-800';
      case 'reversal': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'posted': return 'bg-green-100 text-green-800';
      case 'reversed': return 'bg-orange-100 text-orange-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'posted': return <CheckCircle size={16} className="text-green-600" />;
      case 'reversed': return <RefreshCw size={16} className="text-orange-600" />;
      case 'cancelled': return <XCircle size={16} className="text-red-600" />;
      default: return <Clock size={16} className="text-gray-600" />;
    }
  };

  const formatCurrency = (amount: number) => {
    return `R ${amount.toFixed(2)}`;
  };

  const getTransactionSummary = () => {
    let totalDebit = 0;
    let totalCredit = 0;
    let totalPosted = 0;
    let totalReversed = 0;

    transactions.forEach((t) => {
      if (t.status === 'posted') {
        totalDebit += t.debit_amount || 0;
        totalCredit += t.credit_amount || 0;
        totalPosted++;
      } else if (t.status === 'reversed') {
        totalReversed++;
      }
    });

    return {
      totalDebit,
      totalCredit,
      totalPosted,
      totalReversed,
      netAmount: totalDebit - totalCredit,
    };
  };

  const summary = getTransactionSummary();

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold">Transactions</h1>
          <p className="text-gray-500">View and manage all financial transactions</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleExport}
            disabled={exporting}
            className="btn-secondary flex items-center gap-2"
          >
            {exporting ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <FileSpreadsheet size={18} />
            )}
            Export CSV
          </button>
          <button
            onClick={() => navigate('/accounts')}
            className="btn-primary flex items-center gap-2"
          >
            <Plus size={18} />
            New Transaction
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Total Posted</p>
          <p className="text-2xl font-bold">{summary.totalPosted}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Total Reversed</p>
          <p className="text-2xl font-bold text-orange-600">{summary.totalReversed}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Total Debits</p>
          <p className="text-2xl font-bold text-red-600">{formatCurrency(summary.totalDebit)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Total Credits</p>
          <p className="text-2xl font-bold text-green-600">{formatCurrency(summary.totalCredit)}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search transactions..."
              value={searchTerm}
              onChange={handleSearch}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">All Types</option>
              {transactionTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">All Status</option>
              {statusOptions.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="From Date"
            />
          </div>

          <div>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="To Date"
            />
          </div>
        </div>

        <div className="flex justify-end mt-3">
          <button
            onClick={() => {
              setFilterType('');
              setFilterStatus('');
              setDateFrom('');
              setDateTo('');
              setSearchTerm('');
            }}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Transactions Table */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading transactions...</div>
      ) : filteredTransactions.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <AlertCircle className="mx-auto text-gray-400" size={48} />
          <p className="mt-2 text-gray-500">No transactions found</p>
          <p className="text-sm text-gray-400">Try adjusting your filters or create a new transaction</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Debit</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Credit</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredTransactions.map((t) => (
                  <tr key={t.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-sm">
                      {formatDate(t.transaction_date)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm font-mono">{t.accounts?.account_number || 'N/A'}</div>
                      <div className="text-xs text-gray-500">{t.accounts?.flats?.flat_number || ''}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${getTypeColor(t.transaction_type)}`}>
                        {getTransactionTypeLabel(t.transaction_type)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm max-w-xs truncate" title={t.description}>
                      {t.description}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 font-mono">
                      {t.reference_number || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-red-600 font-medium">
                      {t.debit_amount > 0 ? formatCurrency(t.debit_amount) : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-green-600 font-medium">
                      {t.credit_amount > 0 ? formatCurrency(t.credit_amount) : '-'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${getStatusColor(t.status)}`}>
                        {getStatusIcon(t.status)}
                        {t.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => {
                            setSelectedTransaction(t);
                            setShowDetailModal(true);
                          }}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="View Details"
                        >
                          <Eye size={16} />
                        </button>
                        {t.status === 'posted' && t.transaction_type !== 'reversal' && (
                          <button
                            onClick={() => {
                              setSelectedTransaction(t);
                              setShowReverseModal(true);
                            }}
                            className="p-1.5 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                            title="Reverse Transaction"
                          >
                            <RefreshCw size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Transaction Detail Modal */}
      {showDetailModal && selectedTransaction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Transaction Details</h2>
              <button
                onClick={() => setShowDetailModal(false)}
                className="p-1 hover:bg-gray-100 rounded-lg"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Transaction ID</p>
                  <p className="font-mono text-sm">{selectedTransaction.id}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <span className={`inline-flex items-center gap-1 text-sm font-medium px-2 py-1 rounded-full ${getStatusColor(selectedTransaction.status)}`}>
                    {getStatusIcon(selectedTransaction.status)}
                    {selectedTransaction.status}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Date</p>
                  <p>{formatDate(selectedTransaction.transaction_date)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Type</p>
                  <span className={`text-sm font-medium px-2 py-1 rounded-full ${getTypeColor(selectedTransaction.transaction_type)}`}>
                    {getTransactionTypeLabel(selectedTransaction.transaction_type)}
                  </span>
                </div>
              </div>

              <div>
                <p className="text-sm text-gray-500">Description</p>
                <p>{selectedTransaction.description}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Reference Number</p>
                  <p className="font-mono">{selectedTransaction.reference_number || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Due Date</p>
                  <p>{formatDate(selectedTransaction.due_date)}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Debit Amount</p>
                  <p className="text-lg font-bold text-red-600">
                    {selectedTransaction.debit_amount > 0 ? formatCurrency(selectedTransaction.debit_amount) : 'R 0.00'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Credit Amount</p>
                  <p className="text-lg font-bold text-green-600">
                    {selectedTransaction.credit_amount > 0 ? formatCurrency(selectedTransaction.credit_amount) : 'R 0.00'}
                  </p>
                </div>
              </div>

              {selectedTransaction.notes && (
                <div>
                  <p className="text-sm text-gray-500">Notes</p>
                  <p className="text-sm bg-gray-50 p-2 rounded-lg">{selectedTransaction.notes}</p>
                </div>
              )}

              {selectedTransaction.reversal_of_transaction_id && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                  <p className="text-sm text-orange-700">
                    <RefreshCw size={14} className="inline mr-1" />
                    This transaction is a reversal of:
                  </p>
                  <p className="font-mono text-sm">{selectedTransaction.reversal_of_transaction_id}</p>
                </div>
              )}

              <div className="border-t border-gray-200 pt-4 mt-4">
                <p className="text-sm text-gray-500">Posted By</p>
                <p>{selectedTransaction.created_by || 'System'}</p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowDetailModal(false)}
                className="flex-1 btn-secondary"
              >
                Close
              </button>
              {selectedTransaction.status === 'posted' && selectedTransaction.transaction_type !== 'reversal' && (
                <button
                  onClick={openEditModal}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                >
                  <Plus size={18} />
                  Edit Transaction
                </button>
              )}
              {selectedTransaction.status === 'posted' && selectedTransaction.transaction_type !== 'reversal' && (
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    setShowReverseModal(true);
                  }}
                  className="flex-1 bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors flex items-center justify-center gap-2"
                >
                  <RefreshCw size={18} />
                  Reverse Transaction
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Transaction Modal */}
      {showEditModal && selectedTransaction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Edit Transaction</h2>
              <button
                onClick={() => setShowEditModal(false)}
                className="p-1 hover:bg-gray-100 rounded-lg"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleEditTransaction} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Date</label>
                  <input type="date" value={editForm.transaction_date} onChange={(e) => setEditForm({ ...editForm, transaction_date: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value as Transaction['status'] })} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                    {statusOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select value={editForm.transaction_type} onChange={(e) => setEditForm({ ...editForm, transaction_type: e.target.value as Transaction['transaction_type'] })} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                    {transactionTypes.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reference</label>
                  <input type="text" value={editForm.reference_number} onChange={(e) => setEditForm({ ...editForm, reference_number: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                  <input type="date" value={editForm.due_date} onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Levy Period</label>
                  <input type="month" value={toLevyPeriodInput(editForm.levy_period)} onChange={(e) => setEditForm({ ...editForm, levy_period: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input type="text" value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" required />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Debit Amount</label>
                  <input type="number" min="0" step="0.01" value={editForm.debit_amount} onChange={(e) => setEditForm({ ...editForm, debit_amount: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Credit Amount</label>
                  <input type="number" min="0" step="0.01" value={editForm.credit_amount} onChange={(e) => setEditForm({ ...editForm, credit_amount: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea rows={3} value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>

              <div className="flex gap-3">
                <button type="button" onClick={() => setShowEditModal(false)} className="flex-1 btn-secondary">Cancel</button>
                <button type="submit" disabled={savingEdit} className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
                  {savingEdit ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reverse Modal */}
      {showReverseModal && selectedTransaction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-orange-600">Reverse Transaction</h2>
              <button
                onClick={() => setShowReverseModal(false)}
                className="p-1 hover:bg-gray-100 rounded-lg"
              >
                <X size={24} />
              </button>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-orange-700">
                <AlertCircle size={16} className="inline mr-1" />
                You are about to reverse the following transaction:
              </p>
              <p className="font-medium mt-1">{selectedTransaction.description}</p>
              <p className="text-sm text-gray-600">
                Amount: {selectedTransaction.debit_amount > 0 
                  ? formatCurrency(selectedTransaction.debit_amount)
                  : formatCurrency(selectedTransaction.credit_amount)}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason for Reversal *
              </label>
              <textarea
                required
                value={reverseReason}
                onChange={(e) => setReverseReason(e.target.value)}
                rows={3}
                placeholder="Please explain why this transaction is being reversed..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowReverseModal(false)}
                className="flex-1 btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleReverseTransaction}
                disabled={!reverseReason || reversing}
                className="flex-1 bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {reversing ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Reversing...
                  </>
                ) : (
                  <>
                    <RefreshCw size={18} />
                    Reverse Transaction
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// X icon component
const X = (props: any) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export default Transactions;
